import type { ApolloClient } from "@apollo/client";
import {
  type IssueRow,
  type IssueViewLiveRow,
  IssueViewLiveFragmentDoc,
  IssueViewMediaDataDocument,
  IssueViewMovedDocument,
  IssueViewUpdatedDocument,
} from "#app/lib/data";
import {
  defineSubscription,
  type AnySubscriptionRequest,
} from "#app/lib/subscriptions/subscription-provider";
import { recordAutomationTouch } from "#app/lib/automation-touches";
import {
  identifyIssueInCache,
  writeIssueToCacheViaClient,
} from "#app/hooks/kanban/kanban-cache-utils";

// ---------------------------------------------------------------------------
// Issue-view realtime — registered by the issue view for the open issue's
// workflow, using the dialog-scoped IssueViewUpdated/IssueViewMoved documents
// (issue-view.graphql).
//
// Both events are handled because the server publishes ISSUE_MOVED xor
// ISSUE_UPDATED — a save that edits fields AND changes stage publishes only
// the former, and the open dialog must not go stale on it. Column membership
// on a mounted board stays the board's own IssueMoved handler's job
// (kanban-subscriptions.ts); this handler writes the entity only.
// ---------------------------------------------------------------------------

type IssueViewPayload = IssueRow & IssueViewLiveRow;

const MEDIA_REFETCH_DELAY_MS = 1_500;
const mediaRefetches = new Map<string, ReturnType<typeof setTimeout>>();

function refetchMediaSoon(client: ApolloClient, issueId: string): void {
  clearTimeout(mediaRefetches.get(issueId));
  mediaRefetches.set(
    issueId,
    setTimeout(() => {
      mediaRefetches.delete(issueId);
      void client.query({ fetchPolicy: "network-only", query: IssueViewMediaDataDocument, variables: { issueId } });
    }, MEDIA_REFETCH_DELAY_MS),
  );
}

function writeIssueViewPayload(
  client: ApolloClient,
  { issue, mediaChanged }: { issue: IssueViewPayload; mediaChanged: boolean },
  openIssueId: string,
): void {
  writeIssueToCacheViaClient(client, issue);
  if (mediaChanged && issue.issueId === openIssueId) refetchMediaSoon(client, openIssueId);
  const cacheId = identifyIssueInCache(client.cache, issue.issueId);
  if (!cacheId) return;
  client.cache.writeFragment({
    id: cacheId,
    fragment: IssueViewLiveFragmentDoc,
    fragmentName: "IssueViewLive",
    data: issue,
  });
}

export function issueViewSubscriptions(
  workflowId: string,
  openIssueId: string,
): AnySubscriptionRequest[] {
  const variables = { workflowId };
  return [
    defineSubscription({
      document: IssueViewUpdatedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueUpdated;
        if (!event) return;
        recordAutomationTouch(event, "updated");
        writeIssueViewPayload(client, event, openIssueId);
      },
    }),
    defineSubscription({
      document: IssueViewMovedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueMoved;
        if (!event) return;
        recordAutomationTouch(event, "moved");
        writeIssueViewPayload(client, event, openIssueId);
      },
    }),
  ];
}
